<template>
  <SoDialog
    v-model="value"
    :max-width="500"
    :card-title="`Cancel Appointment ${booking.displayJobNumber}`"
  >
    <div class="d-flex flex-column gap-4">
      <SoSelect
        v-model="selectedInitiator"
        label="Appointment cancelled by"
        placeholder="Select cancellation source"
        :items="initiatorOptions"
        data-testid="cancel-initiator-select"
      />

      <SoSelect
        v-model="selectedReason"
        label="Cancellation reason"
        placeholder="Select cancellation reason"
        :items="reasonOptions"
        :disabled="!selectedInitiator"
        data-testid="cancel-reason-select"
      />
    </div>

    <div class="d-flex justify-end gap-2 mt-10">
      <SoButton title="Back" type="secondary" @click="value = false" />
      <SoButton
        title="Confirm cancellation"
        type="primary"
        :disabled="!isFormValid"
        :loading="cancelBookingsMutator.isPending"
        data-testid="cancel-confirm-btn"
        @click="confirmCancellation"
      />
    </div>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { useSnackbarStore } from "@/stores/snackbar"
  import { errorReporter } from "@/services/ErrorService"
  import { IImbpBooking } from "@/models/imbpBooking/imbpBooking.types"

  export default defineComponent({
    components: {
      SoDialog,
      SoSelect,
      SoButton,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
        required: true,
      },
      booking: {
        type: Object as PropType<IImbpBooking>,
        required: true,
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        current: useCurrentAccountStore(),
        snackbar: useSnackbarStore(),
        selectedInitiator: null as string | null,
        selectedReason: null as string | null,
        cancelBookingsMutator: useCurrentAccountStore().cancelImbpBooking({
          bookingId: this.booking.mopJobNumber,
        }),
      }
    },
    computed: {
      value: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
      cancellationReasons() {
        return this.current.imbpCancellationReasons.data ?? []
      },
      initiatorOptions() {
        const uniqueInitiators = new Map(
          this.cancellationReasons
            .filter((reason) => reason.initiator !== "AES")
            .map((reason) => [reason.initiator, reason.initiatorLabel]),
        )
        return Array.from(uniqueInitiators, ([value, label]) => ({
          title: value === "SOE" ? "So Energy" : label,
          value,
        }))
      },
      reasonOptions() {
        return this.cancellationReasons
          .filter((reason) => reason.initiator === this.selectedInitiator)
          .map((reason) => ({
            title: reason.description,
            value: reason.reason,
          }))
      },
      selectedReasonData() {
        return this.cancellationReasons.find(
          (reason) =>
            reason.reason === this.selectedReason &&
            reason.initiator === this.selectedInitiator,
        )
      },
      isFormValid(): boolean {
        return !!this.selectedInitiator && !!this.selectedReason
      },
    },
    watch: {
      selectedInitiator() {
        this.selectedReason = null
      },
    },
    methods: {
      async confirmCancellation() {
        if (!this.selectedReasonData) return

        try {
          await this.cancelBookingsMutator.mutateAsync({
            reason: this.selectedReasonData.reason,
            initiatedBy: this.selectedReasonData.initiator,
          })
          this.snackbar.addSuccess(
            "Appointment cancelled. The customer will receive a confirmation email.",
          )
        } catch (error) {
          errorReporter.report(error)
          this.snackbar.addError(
            "Failed to cancel the appointment. Please try again.",
          )
        }
      },
    },
  })
</script>

<style scoped>
  :deep(label) {
    font-size: 16px;
  }
</style>
